import { useEffect, useMemo, useState } from "react";
import { api, asArray, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ListChecks, Plus, Pencil, Trash2, X, Save, EyeOff, Eye, Search,
} from "lucide-react";
import { StatusBadge } from "@/components/Primitives";

// Field-definition CRUD (sub-project I, Task 4). A manager builds the
// per-category custom fields tickets can carry -- `update_ticket`/
// `create_ticket` validate against these through `ticket_fields.py`. This
// screen is MANAGE only (service_desk_manager / l3_technician, plus
// super_admin/msp_owner) -- the server refuses everyone else with 403, and
// the nav entry is hidden from anyone the server would refuse (Layout.jsx).
//
// Deleting is a HARD delete, and the server refuses it (409, naming the
// count) while any ticket still carries a value under that key -- there is
// no soft-delete/restore here the way User Management has one, because a
// field with no ticket referencing it orphans nothing. Deactivating is the
// safe, always-available alternative: it stops the field being offered or
// enforced without touching data already stored under it.

// Must match `ticket_fields.FIELD_TYPES` in the backend exactly -- the
// server is the real gate (an unknown type is refused 422), this is only
// the picker's vocabulary.
const FIELD_TYPES = [
  { key: "text", label: "Text" },
  { key: "number", label: "Number" },
  { key: "date", label: "Date (YYYY-MM-DD)" },
  { key: "select", label: "Select (dropdown)" },
  { key: "checkbox", label: "Checkbox" },
];

const inputCls =
  "w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-200 focus:border-zinc-600 outline-none";

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-zinc-950 border border-zinc-800 rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-900 sticky top-0 bg-zinc-950">
          <div className="text-sm font-medium text-zinc-100">{title}</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X size={16} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// A toast is not a durable signal: sonner auto-dismisses in a few seconds,
// uses `aria-live="polite"` (never `role="alert"`), and this app's single
// global `<Toaster>` (App.js) has no `richColors` -- an error toast renders
// the same neutral colour as a success one. Review finding 1: a manager who
// submitted a bad key saw nothing durable, because the only signal was a
// toast that looked like success and was gone before anyone looked back.
// Same visual shape as the page-level banner this file already had for its
// list-load failure -- rendered inside the modal instead, since that is
// where the caller is looking when the refusal happens.
function ErrorBanner({ message }) {
  if (!message) return null;
  return (
    <div className="mb-3 border border-red-900/50 bg-red-950/30 text-red-300 rounded px-4 py-3 text-sm">
      {message}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="mb-3">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-zinc-600 mt-1">{hint}</div>}
    </div>
  );
}

const EMPTY = {
  key: "", label: "", type: "text", options: "", category: "",
  required_on_close: false, position: 0, active: true,
};

// "asset_tag" from a form's raw "Asset Tag" -- the server is the actual
// gate (a bad shape is refused 422 naming the rule), this only saves a
// manager from typing the underscored form by hand for the common case.
function slugify(s) {
  return (s || "")
    .trim().toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^([0-9])/, "f_$1");
}

function toPayload(form) {
  return {
    key: form.key.trim(),
    label: form.label.trim(),
    type: form.type,
    options: form.type === "select"
      ? form.options.split(",").map((o) => o.trim()).filter(Boolean)
      : [],
    category: form.category.trim() || null,
    required_on_close: !!form.required_on_close,
    position: Number(form.position) || 0,
    active: !!form.active,
  };
}

export default function TicketFieldSettings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [keyTouched, setKeyTouched] = useState(false);
  const [createErr, setCreateErr] = useState("");
  const [editErr, setEditErr] = useState("");

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/ticket-field-defs");
      setRows(asArray(r.data));
      setErr("");
    } catch (e) {
      // An unreachable list must not look like an empty one.
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  const shown = useMemo(() => rows.filter((d) => {
    const s = `${d.key} ${d.label} ${d.category || ""} ${d.type}`.toLowerCase();
    return !q || s.includes(q.toLowerCase());
  }), [rows, q]);

  async function create() {
    setCreateErr("");
    // `required` on the Key input is semantic only -- there is no `<form>`
    // wrapping this modal, so it does not stop a click-driven submit the
    // way it would inside one. The real guard is here, same idiom as
    // `ServiceDesk.jsx`'s own `if (!payload.name) { toast.error(...);
    // return; }`: catch a bare submit before the request is even sent,
    // rather than relying on the server's 422 (which a bare key WOULD
    // still correctly refuse, but only after a round trip).
    if (!form.key.trim()) {
      setCreateErr("Key is required.");
      return;
    }
    try {
      await api.post("/ticket-field-defs", toPayload(form));
      toast.success(`Created field '${form.key.trim()}'`);
      setCreating(false); setForm(EMPTY); setKeyTouched(false); load();
    } catch (e) {
      // `formatApiError` is required here, not optional tidiness: a bare
      // submit sends an empty key, Pydantic's own `Field(min_length=1)`
      // refuses it before this route's handler ever runs, and FastAPI's
      // 422 body carries `detail` as an ARRAY of objects, not a string.
      // The raw `e?.response?.data?.detail` handed that array straight to
      // `toast.error`, which renders it as a React child -- "Objects are
      // not valid as a React child", a crash, since `<Toaster>` sits
      // outside any `ErrorBoundary`. `formatApiError` normalises every
      // shape (string, array-of-{msg}, object) to one string. Kept as a
      // PERSISTENT banner inside the modal (see `ErrorBanner` above) rather
      // than only a toast -- a toast auto-dismisses and, without
      // `richColors` on the app's one global `<Toaster>`, an error toast is
      // the same neutral colour as a success one.
      setCreateErr(formatApiError(e));
      toast.error(formatApiError(e));
    }
  }

  async function saveEdit() {
    setEditErr("");
    try {
      const body = toPayload(editing);
      delete body.key; // never settable via update -- see the page header note
      const r = await api.patch(`/ticket-field-defs/${editing.id}`, body);
      // `update_field_def` returns `{"ok": true, "changed": {}}` (two keys)
      // when nothing changed, and the full stored document otherwise --
      // `Object.keys(r.data || {}).length` is non-zero either way, so that
      // check could never report "Nothing changed". Read `r.data.changed`
      // explicitly instead, same idiom as `Users.jsx`'s `saveEdit`.
      const n = Object.keys(r.data?.changed || {}).length;
      toast.success(n ? `Updated ${n} field${n > 1 ? "s" : ""}` : "Nothing changed");
      setEditing(null); load();
    } catch (e) {
      setEditErr(formatApiError(e));
      toast.error(formatApiError(e));
    }
  }

  async function toggleActive(d) {
    try {
      await api.patch(`/ticket-field-defs/${d.id}`, { active: !d.active });
      toast.success(d.active ? "Deactivated" : "Reactivated");
      load();
    } catch (e) {
      // No modal for a row action -- the durable signal is the same
      // page-level banner the initial list load already uses.
      setErr(formatApiError(e));
      toast.error(formatApiError(e));
    }
  }

  async function remove(d) {
    if (!window.confirm(
      `Delete field '${d.key}'?\n\nThis is a hard delete -- refused if any ticket still holds a value for it.`)) return;
    try {
      await api.delete(`/ticket-field-defs/${d.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      // The server names the count in its 409 -- surface it verbatim rather
      // than a generic message, so "Deactivate instead" reads as an
      // instruction, not a mystery. Persisted in the page banner too, same
      // reasoning as `toggleActive` above.
      setErr(formatApiError(e));
      toast.error(formatApiError(e), { duration: 6000 });
    }
  }

  return (
    <div className="p-6" data-testid="ticket-field-settings-page">
      <div className="flex items-center gap-3 mb-5">
        <ListChecks size={18} className="text-zinc-400" />
        <div>
          <h1 className="text-lg font-display font-semibold text-zinc-50">Ticket Field Settings</h1>
          <div className="text-xs text-zinc-500">
            Custom fields tickets carry, per category. Enforced by the server on create and close.
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-2.5 text-zinc-600" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search fields…"
              className="bg-zinc-900 border border-zinc-800 rounded pl-8 pr-3 py-1.5 text-sm text-zinc-200 outline-none focus:border-zinc-600 w-56" />
          </div>
          <button onClick={() => { setForm(EMPTY); setKeyTouched(false); setCreateErr(""); setCreating(true); }}
            data-testid="field-def-new"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm">
            <Plus size={14} /> New field
          </button>
        </div>
      </div>

      {err && (
        <div className="mb-4 border border-red-900/50 bg-red-950/30 text-red-300 rounded px-4 py-3 text-sm">
          {err}
        </div>
      )}

      <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono border-b border-zinc-900">
              <th className="text-left px-4 py-2.5">Key</th>
              <th className="text-left px-4 py-2.5">Label</th>
              <th className="text-left px-4 py-2.5">Type</th>
              <th className="text-left px-4 py-2.5">Category</th>
              <th className="text-left px-4 py-2.5">Required at close</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-600 text-sm">Loading…</td></tr>
            )}
            {!loading && !shown.length && !err && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-600 text-sm">
                No field definitions yet -- create one to start collecting structured data on tickets.
              </td></tr>
            )}
            {shown.map((d) => (
              <tr key={d.id} data-testid={`field-def-row-${d.id}`}
                className={`border-b border-zinc-900/70 hover:bg-zinc-900/40 ${!d.active ? "opacity-50" : ""}`}>
                <td className="px-4 py-2.5 font-mono text-[12.5px] text-zinc-300">{d.key}</td>
                <td className="px-4 py-2.5 text-zinc-200">{d.label}</td>
                <td className="px-4 py-2.5 text-zinc-400">
                  {FIELD_TYPES.find((t) => t.key === d.type)?.label || d.type}
                  {d.type === "select" && d.options?.length > 0 && (
                    <div className="text-[11px] text-zinc-600">{d.options.join(", ")}</div>
                  )}
                </td>
                <td className="px-4 py-2.5 text-zinc-500 text-[12.5px]">{d.category || "Every category"}</td>
                <td className="px-4 py-2.5 text-zinc-400">{d.required_on_close ? "Yes" : "No"}</td>
                <td className="px-4 py-2.5">
                  <StatusBadge status={d.active ? "active" : "disabled"} />
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => { setEditErr(""); setEditing({ ...d, options: (d.options || []).join(", ") }); }}
                      title="Edit" data-testid={`field-def-edit-${d.id}`}
                      className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-zinc-100">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => toggleActive(d)}
                      title={d.active ? "Deactivate" : "Reactivate"}
                      data-testid={`field-def-toggle-${d.id}`}
                      className="p-1.5 rounded hover:bg-zinc-800 text-amber-400">
                      {d.active ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                    <button onClick={() => remove(d)} title="Delete"
                      data-testid={`field-def-delete-${d.id}`}
                      className="p-1.5 rounded hover:bg-zinc-800 text-red-400">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && (
        <Modal title="New field" onClose={() => setCreating(false)}>
          <ErrorBanner message={createErr} />
          <Field label="Key" hint="Lowercase letters, digits and underscores only. Cannot be changed later.">
            <input className={inputCls} value={form.key} required
              onChange={(e) => { setKeyTouched(true); setForm({ ...form, key: e.target.value }); }}
              placeholder="asset_tag" />
          </Field>
          <Field label="Label">
            <input className={inputCls} value={form.label}
              onChange={(e) => {
                const label = e.target.value;
                setForm((f) => ({ ...f, label, key: keyTouched ? f.key : slugify(label) }));
              }}
              placeholder="Asset Tag" />
          </Field>
          <Field label="Type">
            <select className={inputCls} value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {FIELD_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </Field>
          {form.type === "select" && (
            <Field label="Options" hint="Comma-separated. At least one is required for a select field.">
              <input className={inputCls} value={form.options}
                onChange={(e) => setForm({ ...form, options: e.target.value })}
                placeholder="low, medium, high" />
            </Field>
          )}
          <Field label="Category" hint="Leave blank to apply this field to every category.">
            <input className={inputCls} value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder="hardware" />
          </Field>
          <Field label="Position" hint="Lower numbers appear first on the ticket form.">
            <input type="number" className={inputCls} value={form.position}
              onChange={(e) => setForm({ ...form, position: e.target.value })} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-zinc-300 mt-2 mb-1 select-none">
            <input type="checkbox" checked={form.required_on_close}
              onChange={(e) => setForm({ ...form, required_on_close: e.target.checked })} />
            Required before a ticket in this category can close
          </label>
          <button onClick={create} data-testid="field-def-create-submit"
            className="mt-3 w-full py-2 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm flex items-center justify-center gap-2">
            <Save size={14} /> Create field
          </button>
        </Modal>
      )}

      {editing && (
        <Modal title={`Edit '${editing.key}'`} onClose={() => setEditing(null)}>
          <ErrorBanner message={editErr} />
          <Field label="Key" hint="Not editable -- tickets already store values under this key.">
            <input className={inputCls} value={editing.key} disabled
              style={{ opacity: 0.6, cursor: "not-allowed" }} />
          </Field>
          <Field label="Label">
            <input className={inputCls} value={editing.label}
              onChange={(e) => setEditing({ ...editing, label: e.target.value })} />
          </Field>
          <Field label="Type">
            <select className={inputCls} value={editing.type}
              onChange={(e) => setEditing({ ...editing, type: e.target.value })}>
              {FIELD_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </Field>
          {editing.type === "select" && (
            <Field label="Options" hint="Comma-separated. At least one is required for a select field.">
              <input className={inputCls} value={editing.options}
                onChange={(e) => setEditing({ ...editing, options: e.target.value })} />
            </Field>
          )}
          <Field label="Category" hint="Leave blank to apply this field to every category.">
            <input className={inputCls} value={editing.category || ""}
              onChange={(e) => setEditing({ ...editing, category: e.target.value })} />
          </Field>
          <Field label="Position">
            <input type="number" className={inputCls} value={editing.position}
              onChange={(e) => setEditing({ ...editing, position: e.target.value })} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-zinc-300 mt-2 mb-1 select-none">
            <input type="checkbox" checked={editing.required_on_close}
              onChange={(e) => setEditing({ ...editing, required_on_close: e.target.checked })} />
            Required before a ticket in this category can close
          </label>
          <div className="flex items-center justify-between py-2 border-t border-zinc-900 mt-2">
            <div>
              <div className="text-sm text-zinc-200">Active</div>
              <div className="text-[11px] text-zinc-600">Off = not offered or enforced. Stored values are kept.</div>
            </div>
            <button onClick={() => setEditing({ ...editing, active: !editing.active })}
              className={`rounded-full transition-colors relative shrink-0 ${editing.active ? "bg-emerald-500/70" : "bg-zinc-700"}`}
              style={{ height: 22, width: 40 }}>
              <span className={`absolute top-0.5 w-[18px] h-[18px] rounded-full bg-white transition-all ${editing.active ? "left-[20px]" : "left-0.5"}`} />
            </button>
          </div>
          <button onClick={saveEdit} data-testid="field-def-edit-submit"
            className="mt-3 w-full py-2 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm flex items-center justify-center gap-2">
            <Save size={14} /> Save changes
          </button>
        </Modal>
      )}
    </div>
  );
}
